import type { ReactNode } from 'react'
import { useTranslation } from 'react-i18next'
import { Spinner } from '../../components/Spinner'
import { ApiError } from '../../lib/api'

/* ─── The three states every tab distinguishes ───

   Never a spinner that never resolves, and never a stack trace. The read path
   already answers 200-with-nothing for a tenant whose collector or compaction job
   has not written any Parquet yet (`_fetch` translates DuckDB's "No files found"),
   so a deployed environment with no data renders honestly rather than erroring.

   **error and empty are deliberately different messages.** "We couldn't load this"
   and "there is nothing here yet" call for opposite responses from the admin, and
   collapsing them into one reassuring empty state is how a broken query gets
   mistaken for a quiet tenant. */

export function TabLoading() {
  return (
    <div style={{ display: 'flex', justifyContent: 'center', padding: '48px 0' }}>
      <Spinner />
    </div>
  )
}

/**
 * The server's own words, but only when they are a 400.
 *
 * A rejected *request* is the admin's to fix — "the period must not exceed 365
 * days" tells them to narrow the range, where the generic message leaves them
 * re-clicking a date picker that will never work. `SEC-ERR-1` designates these
 * validation messages as safe to surface for exactly that reason.
 *
 * Every other status is deliberately excluded. A 500's message describes the
 * server's internals, not the caller's mistake, and there is nothing the reader
 * could do with it but forward it somewhere it should not go.
 */
export function requestErrorDetail(error: unknown): string | null {
  return error instanceof ApiError && error.status === 400 && error.message
    ? error.message
    : null
}

export function TabError({ error, onRetry }: { error?: unknown; onRetry?: () => void }) {
  const { t } = useTranslation()
  const detail = requestErrorDetail(error)
  return (
    <div
      role="alert"
      style={{ padding: '32px 14px', textAlign: 'center', color: 'var(--text-secondary)', fontSize: 13 }}
    >
      <p style={{ margin: 0 }}>{t('mcpAnalytics.detail.loadFailed')}</p>
      {detail && (
        <p style={{ margin: '6px 0 0', color: 'var(--text-tertiary)' }}>{detail}</p>
      )}
      {onRetry && (
        <button type="button" className="btn btn-ghost" style={{ marginTop: 12 }} onClick={onRetry}>
          {t('mcpAnalytics.detail.refresh')}
        </button>
      )}
    </div>
  )
}

/** "No activity yet" — the tenant has produced nothing at all, as opposed to
 *  "nothing matches these filters", which each tab words for itself. */
export function TabNoDataYet() {
  const { t } = useTranslation()
  return (
    <div style={{ padding: '32px 14px', textAlign: 'center', color: 'var(--text-tertiary)', fontSize: 13 }}>
      <p style={{ margin: 0, color: 'var(--text-secondary)' }}>{t('mcpAnalytics.detail.noDataYet')}</p>
      <p style={{ margin: '6px 0 0' }}>{t('mcpAnalytics.detail.noDataYetHint')}</p>
    </div>
  )
}

/**
 * Renders whichever of loading / error / content applies.
 *
 * The empty *state* is left to the caller because the three tabs word it
 * differently and, on Invoke Activity, split it further (no matches for a term vs
 * no invoke calls at all — MS-702 AC20).
 */
export function TabState({
  isLoading,
  isError,
  error,
  onRetry,
  children,
}: {
  isLoading: boolean
  isError: boolean
  /** The thrown value, so a rejected request can say what was wrong with it. */
  error?: unknown
  onRetry?: () => void
  children: ReactNode
}) {
  if (isLoading) return <TabLoading />
  if (isError) return <TabError error={error} onRetry={onRetry} />
  return <>{children}</>
}
